<template>
  <div class="bg-background text-foreground min-h-screen">
    <!-- Toast Notification -->
    <Toast
      :show="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      @close="showToast = false"
    />

    <div class="w-full px-4 sm:px-6 py-5">

      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-5">
        <div class="flex items-start gap-3 min-w-0">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-eye-slash text-primary text-lg"></i>
          </div>
          <div class="min-w-0">
            <h1 class="text-lg sm:text-xl font-bold leading-tight">Hidden Jobs</h1>
            <p class="text-sm text-muted-foreground leading-snug">
              Jobs you've marked as "not interested". Unhide any of these to see them in your matching jobs again.
            </p>
          </div>
        </div>
        <span
          v-if="!loading && !error && hiddenJobs.length > 0"
          class="flex-shrink-0 self-start inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border"
        >
          {{ hiddenJobs.length }}
        </span>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border">
        <div
          v-for="i in 5"
          :key="i"
          class="p-4 flex items-center gap-3"
        >
          <div class="w-9 h-9 rounded-lg bg-muted animate-pulse flex-shrink-0"></div>
          <div class="flex-1 space-y-2 min-w-0">
            <div class="h-3 w-2/5 rounded bg-muted animate-pulse"></div>
            <div class="h-3 w-1/4 rounded bg-muted animate-pulse"></div>
          </div>
          <div class="h-8 w-20 rounded-lg bg-muted animate-pulse flex-shrink-0"></div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 mb-5"
      >
        <div class="flex items-start gap-3">
          <i class="fas fa-exclamation-triangle text-red-600 dark:text-red-400 mt-0.5"></i>
          <div>
            <p class="font-semibold text-red-700 dark:text-red-300 text-sm">Something went wrong</p>
            <p class="text-sm text-red-700/90 dark:text-red-300/90 mt-0.5">{{ error }}</p>
            <button
              @click="fetchHiddenJobs"
              class="mt-3 px-4 py-1.5 rounded-lg border border-red-500/30 bg-card hover:bg-red-500/10 text-red-700 dark:text-red-300 text-sm font-medium transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="hiddenJobs.length === 0"
        class="bg-card border border-border rounded-xl"
      >
        <div class="flex flex-col items-center justify-center py-16 text-center">
          <div class="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-3">
            <i class="fas fa-eye-slash text-muted-foreground text-xl"></i>
          </div>
          <div class="text-sm font-semibold mb-1">No hidden jobs</div>
          <div class="text-[12px] text-muted-foreground mb-6 max-w-xs mx-auto">
            Jobs you mark "Not interested" will appear here so you can bring them back anytime.
          </div>
          <router-link
            to="/job-filters"
            class="inline-flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold rounded-lg transition"
          >
            <i class="fas fa-search text-xs"></i>
            Browse Jobs
          </router-link>
        </div>
      </div>

      <!-- Results List -->
      <div v-else class="bg-card border border-border rounded-xl overflow-hidden">

        <!-- Mobile cards -->
        <div class="grid grid-cols-1 divide-y divide-border lg:hidden">
          <div
            v-for="job in paginatedHiddenJobs"
            :key="job.id"
            class="p-4"
          >
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <span class="text-primary text-[11px] font-bold">{{ employerInitials(job) }}</span>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5">
                  <span v-if="getSourceLogo(jobSource(job))" class="h-4 w-4 rounded-full bg-white dark:bg-white/90 ring-1 ring-border/60 flex items-center justify-center shrink-0 overflow-hidden">
                    <img :src="getSourceLogo(jobSource(job))" :alt="getSourceLabel(jobSource(job))" class="h-full w-full object-contain" />
                  </span>
                  <h3 class="text-sm font-semibold text-foreground leading-snug line-clamp-2 min-w-0">
                    <router-link :to="getJobDetailPath(job)" class="hover:text-primary">
                      {{ job.job_details?.title || job.title || "Untitled Position" }}
                    </router-link>
                  </h3>
                </div>
                <p class="text-xs text-muted-foreground mt-0.5 truncate">
                  {{ employerName(job) }}
                </p>
              </div>
            </div>
            <div class="mt-3 flex items-center justify-between gap-2">
              <span v-if="job.date_hidden" class="text-[11px] text-muted-foreground">
                <i class="fas fa-eye-slash text-[9px] mr-1"></i>Hidden {{ relativeTime(job.date_hidden) }}
              </span>
              <button
                @click="askUnhideJob(job)"
                class="shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground transition"
              >
                <i class="fas fa-eye text-[10px]"></i>
                Unhide
              </button>
            </div>
          </div>
        </div>

        <!-- Desktop table -->
        <div class="hidden lg:block overflow-x-auto">
          <table class="min-w-full divide-y divide-border table-fixed">
            <colgroup>
              <col class="w-[42%]" />
              <col class="w-[24%]" />
              <col class="w-[17%]" />
              <col class="w-[17%]" />
            </colgroup>
            <thead class="bg-muted/50 border-b border-border">
              <tr>
                <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Job
                </th>
                <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Employer
                </th>
                <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Hidden
                </th>
                <th scope="col" class="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-card divide-y divide-border">
              <tr
                v-for="job in paginatedHiddenJobs"
                :key="job.id"
                class="hover:bg-muted/30 transition"
              >
                <td class="px-5 py-4">
                  <div class="flex items-center gap-1.5 min-w-0">
                    <span v-if="getSourceLogo(jobSource(job))" class="h-4 w-4 rounded-full bg-white dark:bg-white/90 ring-1 ring-border/60 flex items-center justify-center shrink-0 overflow-hidden">
                      <img :src="getSourceLogo(jobSource(job))" :alt="getSourceLabel(jobSource(job))" class="h-full w-full object-contain" />
                    </span>
                    <router-link :to="getJobDetailPath(job)" class="text-sm font-semibold text-foreground hover:text-primary truncate">
                      {{ job.job_details?.title || job.title || "Untitled Position" }}
                    </router-link>
                  </div>
                </td>
                <td class="px-5 py-4 text-sm text-foreground truncate">
                  {{ employerName(job) }}
                </td>
                <td class="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">
                  {{ job.date_hidden ? relativeTime(job.date_hidden) : '—' }}
                </td>
                <td class="px-5 py-4 text-right">
                  <button
                    @click="askUnhideJob(job)"
                    class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground transition"
                  >
                    <i class="fas fa-eye text-[10px]"></i>
                    Unhide
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination bar -->
        <div
          v-if="totalPages > 1"
          class="px-5 py-3.5 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div class="text-xs text-muted-foreground">
            Showing
            <span class="font-semibold text-foreground">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
            &ndash;
            <span class="font-semibold text-foreground">{{ Math.min(currentPage * itemsPerPage, sortedHiddenJobs.length) }}</span>
            of
            <span class="font-semibold text-foreground">{{ sortedHiddenJobs.length }}</span>
          </div>

          <nav class="flex items-center gap-1 bg-muted/50 border border-border rounded-lg p-1 self-start sm:self-auto">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm"
              :class="[
                currentPage === 1
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              <i class="fas fa-chevron-left text-xs"></i>
            </button>

            <button
              v-for="page in displayedPages"
              :key="page"
              @click="goToPage(page)"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm font-medium"
              :class="[
                page === currentPage
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              {{ page }}
            </button>

            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="w-8 h-8 rounded-md flex items-center justify-center transition text-sm"
              :class="[
                currentPage === totalPages
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              <i class="fas fa-chevron-right text-xs"></i>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Unhide confirmation modal -->
    <div
      v-if="jobPendingUnhide"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card rounded-xl border border-border shadow-xl max-w-sm w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
            <i class="fas fa-eye text-sm"></i>
          </div>
          <div>
            <h3 class="text-base font-semibold text-foreground">Unhide this job?</h3>
            <p class="text-xs text-muted-foreground mt-0.5">It'll show up in your matching jobs again.</p>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <button
            @click="confirmUnhideJob"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <i class="fas fa-eye text-xs"></i>
            Yes, unhide it
          </button>
          <button
            @click="jobPendingUnhide = null"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import Toast from "@/components/Toast.vue";
import { buildJobDetailsPath } from "@/utils/slug";

export default {
  name: "HiddenJobsPage",
  components: {
    Toast,
  },
  setup() {
    const store = useStore();

    const showToast = ref(false);
    const toastType = ref("success");
    const toastTitle = ref("");
    const toastMessage = ref("");
    const jobPendingUnhide = ref(null);

    const SOURCE_LABELS = {
      olj: "External",
      linkedin: "LinkedIn",
      indeed: "Indeed",
      upwork: "Upwork",
      jobstreet: "JobStreet",
      cloudstaff: "Cloudstaff",
      emapta: "Emapta",
      bossjob: "BossJob.ph",
      kalibrr: "Kalibrr",
      findtalent: "FindTalent.ph",
      hiretalent: "HireTalent.ph",
      ofmjobs: "OFM Jobs",
    };

    const jobSource = (job) => job?.job_details?.source || job?.source || null;

    const getSourceLabel = (source) => SOURCE_LABELS[source] || source || "Unknown";

    const getSourceLogo = (source) => {
      const map = {
        olj: "/images/job-source-logos/olj-icon-2.png",
        jobstreet: "/images/job-source-logos/jobstreet-icon.png",
        cloudstaff: "/images/job-source-logos/cloudstaff-logo-emblem.svg",
        emapta: "/images/job-source-logos/emapta-logo.svg",
        bossjob: "/images/job-source-logos/bossjob-icon.png",
        kalibrr: "/images/job-source-logos/kalibrr-logo.png",
        findtalent: "/images/job-source-logos/findtalent-icon.svg",
        hiretalent: "/images/job-source-logos/hiretalent-logo.jpg",
        ofmjobs: "/images/job-source-logos/ofmjobs-icon.svg",
        upwork: "/images/job-source-logos/upwork-icon.svg",
      };
      return map[source] ?? null;
    };

    const hiddenJobs = computed(() => store.getters["hiddenJobs/hiddenJobs"]);
    const loading = computed(() => store.getters["hiddenJobs/loading"]);
    const error = computed(() => store.getters["hiddenJobs/error"]);

    const currentPage = ref(1);
    const itemsPerPage = ref(10);

    const sortedHiddenJobs = computed(() => {
      const list = Array.isArray(hiddenJobs.value) ? [...hiddenJobs.value] : [];
      const getHiddenTime = (item) => {
        const raw = item?.date_hidden;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      list.sort((a, b) => getHiddenTime(b) - getHiddenTime(a));
      return list;
    });

    const totalPages = computed(() => {
      const total = sortedHiddenJobs.value.length;
      return Math.max(1, Math.ceil(total / itemsPerPage.value));
    });

    const paginatedHiddenJobs = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return sortedHiddenJobs.value.slice(start, end);
    });

    const displayedPages = computed(() => {
      const total = totalPages.value;
      const current = currentPage.value;
      const windowSize = 5;

      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      let start = Math.max(1, current - Math.floor(windowSize / 2));
      let end = start + windowSize - 1;

      if (end > total) {
        end = total;
        start = end - windowSize + 1;
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    const goToPage = (page) => {
      if (typeof page !== "number") return;
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value += 1;
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value -= 1;
      }
    };

    watch(
      () => sortedHiddenJobs.value.length,
      () => {
        if (currentPage.value > totalPages.value) {
          currentPage.value = totalPages.value;
        }
      },
    );

    const getJobDetailPath = (jobItem) => {
      if (!jobItem) {
        return buildJobDetailsPath("", "");
      }
      const id = jobItem.job_details?.id ?? jobItem.job?.id ?? jobItem.job_id ?? jobItem.id;
      const title = jobItem.job_details?.title ?? jobItem.job?.title ?? jobItem.title;
      return buildJobDetailsPath(title, id);
    };

    const employerName = (job) => {
      const employer = job?.job_details?.employer || job?.employer || null;
      if (!employer) return "Employer";
      return typeof employer === "string" ? employer : employer.name || "Employer";
    };

    const employerInitials = (job) => {
      const employer =
        job?.job_details?.employer || job?.employer || null;
      if (!employer) return "CO";

      const employerName =
        typeof employer === "string" ? employer : employer.name;
      if (!employerName) return "CO";

      return employerName
        .split(" ")
        .filter(Boolean)
        .map((word) => word[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    };

    const relativeTime = (dateStr) => {
      if (!dateStr) return "";
      const then = new Date(dateStr).getTime();
      if (!Number.isFinite(then)) return "";
      const diffMs = Date.now() - then;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return "today";
      if (diffDays === 1) return "1 day ago";
      return `${diffDays} days ago`;
    };

    onMounted(() => {
      fetchHiddenJobs();
    });

    const fetchHiddenJobs = async () => {
      try {
        await store.dispatch("hiddenJobs/fetchHiddenJobs");
      } catch (err) {
        console.error("Error fetching hidden jobs:", err);
      }
    };

    const askUnhideJob = (job) => {
      jobPendingUnhide.value = job;
    };

    const confirmUnhideJob = async () => {
      const job = jobPendingUnhide.value;
      jobPendingUnhide.value = null;
      if (!job) return;
      await unhideJob(job);
    };

    const unhideJob = async (job) => {
      try {
        const jobId = job.job_details?.id ?? job.job_id ?? job.job ?? job.id;
        const result = await store.dispatch("hiddenJobs/unhideJob", jobId);

        if (result && result.success) {
          toastType.value = "success";
          toastTitle.value = "Job unhidden";
          toastMessage.value = "This job will show up in your matching jobs again.";
        } else {
          toastType.value = "error";
          toastTitle.value = "Error";
          toastMessage.value = "Could not unhide this job. Please try again.";
        }
        showToast.value = true;
        setTimeout(() => {
          showToast.value = false;
        }, 3000);
      } catch (err) {
        console.error("Error unhiding job:", err);
        toastType.value = "error";
        toastTitle.value = "Error";
        toastMessage.value = "An error occurred while unhiding the job. Please try again.";
        showToast.value = true;
        setTimeout(() => {
          showToast.value = false;
        }, 3000);
      }
    };

    return {
      hiddenJobs,
      loading,
      error,
      showToast,
      toastType,
      toastTitle,
      toastMessage,
      fetchHiddenJobs,
      unhideJob,
      jobPendingUnhide,
      askUnhideJob,
      confirmUnhideJob,
      getJobDetailPath,
      employerName,
      employerInitials,
      jobSource,
      getSourceLabel,
      getSourceLogo,
      relativeTime,
      sortedHiddenJobs,
      currentPage,
      itemsPerPage,
      totalPages,
      paginatedHiddenJobs,
      displayedPages,
      goToPage,
      nextPage,
      prevPage,
    };
  },
};
</script>
